/* @ts-self-types="./rstar_geodetic_wasm.d.ts" */

/**
 * A geodetic R-tree over linestrings.
 */
export class LineStringTree {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        LineStringTreeFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_linestringtree_free(ptr, 0);
    }
    /**
     * The number of linestrings in the tree.
     * @returns {number}
     */
    get length() {
        const ret = wasm.linestringtree_length(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * The input position of the linestring nearest to `(lon, lat)` by great-circle
     * distance, or `undefined` if the tree is empty.
     * @param {number} lon
     * @param {number} lat
     * @returns {number | undefined}
     */
    nearest(lon, lat) {
        const ret = wasm.linestringtree_nearest(this.__wbg_ptr, lon, lat);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] === Number.MAX_SAFE_INTEGER ? undefined : ret[0];
    }
    /**
     * The `k` nearest linestrings to `(lon, lat)` by great-circle distance, nearest first,
     * as a `{ indices, distances }` object. Fewer than `k` are returned if the tree
     * holds fewer linestrings.
     * @param {number} lon
     * @param {number} lat
     * @param {number} k
     * @returns {Neighbors}
     */
    nearestN(lon, lat, k) {
        const ret = wasm.linestringtree_nearestN(this.__wbg_ptr, lon, lat, k);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * The linestring nearest to `(lon, lat)` as a `{ index, distance }` object, or
     * `undefined` if the tree is empty.
     * @param {number} lon
     * @param {number} lat
     * @returns {Neighbor | undefined}
     */
    nearestWithDistance(lon, lat) {
        const ret = wasm.linestringtree_nearestWithDistance(this.__wbg_ptr, lon, lat);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Builds a tree from interleaved `[lon0, lat0, lon1, lat1, ...]` vertex coordinates
     * in degrees, partitioned into linestrings by `offsets`: a CSR array with one entry
     * per linestring plus a final end marker, so linestring `i` spans vertices
     * `offsets[i]..offsets[i + 1]` (the GeoArrow layout). Throws a `GeodeticError` on an
     * invalid offset array, an out-of-range or non-finite coordinate, or a linestring
     * with fewer than two vertices.
     * @param {Float64Array} coords
     * @param {Uint32Array} offsets
     */
    constructor(coords, offsets) {
        const ptr0 = passArrayF64ToWasm0(coords, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray32ToWasm0(offsets, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.linestringtree_new(ptr0, len0, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        LineStringTreeFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Every linestring within `radiusMetres` great-circle metres of `(lon, lat)`, as a
     * `{ indices, distances }` object.
     * @param {number} lon
     * @param {number} lat
     * @param {number} radius_metres
     * @returns {Neighbors}
     */
    withinDistance(lon, lat, radius_metres) {
        const ret = wasm.linestringtree_withinDistance(this.__wbg_ptr, lon, lat, radius_metres);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
}
if (Symbol.dispose) LineStringTree.prototype[Symbol.dispose] = LineStringTree.prototype.free;

/**
 * A geodetic R-tree over points.
 */
export class PointTree {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        PointTreeFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_pointtree_free(ptr, 0);
    }
    /**
     * The input positions of every point inside the longitude/latitude rectangle from
     * `(minLon, minLat)` to `(maxLon, maxLat)`. `minLon > maxLon` denotes a window
     * crossing the antimeridian (RFC 7946); `minLat <= maxLat` is required.
     * @param {number} min_lon
     * @param {number} min_lat
     * @param {number} max_lon
     * @param {number} max_lat
     * @returns {Uint32Array}
     */
    inRectangle(min_lon, min_lat, max_lon, max_lat) {
        const ret = wasm.pointtree_inRectangle(this.__wbg_ptr, min_lon, min_lat, max_lon, max_lat);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayU32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * The number of points in the tree.
     * @returns {number}
     */
    get length() {
        const ret = wasm.pointtree_length(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * The input position of the nearest point to `(lon, lat)` by great-circle distance,
     * or `undefined` if the tree is empty.
     * @param {number} lon
     * @param {number} lat
     * @returns {number | undefined}
     */
    nearest(lon, lat) {
        const ret = wasm.pointtree_nearest(this.__wbg_ptr, lon, lat);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] === Number.MAX_SAFE_INTEGER ? undefined : ret[0];
    }
    /**
     * The `k` nearest points to `(lon, lat)` by great-circle distance, nearest first,
     * as a `{ indices, distances }` object. Fewer than `k` are returned if the tree
     * holds fewer points.
     * @param {number} lon
     * @param {number} lat
     * @param {number} k
     * @returns {Neighbors}
     */
    nearestN(lon, lat, k) {
        const ret = wasm.pointtree_nearestN(this.__wbg_ptr, lon, lat, k);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * The input position of the nearest point to `(lon, lat)` by WGS84-ellipsoid
     * geodesic distance, or `undefined` if the tree is empty.
     * @param {number} lon
     * @param {number} lat
     * @returns {number | undefined}
     */
    nearestWgs84(lon, lat) {
        const ret = wasm.pointtree_nearestWgs84(this.__wbg_ptr, lon, lat);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] === Number.MAX_SAFE_INTEGER ? undefined : ret[0];
    }
    /**
     * The nearest point to `(lon, lat)` by great-circle distance as a
     * `{ index, distance }` object, or `undefined` if the tree is empty.
     * @param {number} lon
     * @param {number} lat
     * @returns {Neighbor | undefined}
     */
    nearestWithDistance(lon, lat) {
        const ret = wasm.pointtree_nearestWithDistance(this.__wbg_ptr, lon, lat);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * The nearest point to `(lon, lat)` on the WGS84 ellipsoid as a `{ index, distance }`
     * object, or `undefined` if the tree is empty.
     * @param {number} lon
     * @param {number} lat
     * @returns {Neighbor | undefined}
     */
    nearestWithDistanceWgs84(lon, lat) {
        const ret = wasm.pointtree_nearestWithDistanceWgs84(this.__wbg_ptr, lon, lat);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Builds a tree from interleaved `[lon0, lat0, lon1, lat1, ...]` coordinates in
     * degrees. Throws a `GeodeticError` if the array has odd length or a coordinate is
     * out of range or not finite.
     * @param {Float64Array} coords
     */
    constructor(coords) {
        const ptr0 = passArrayF64ToWasm0(coords, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.pointtree_new(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        PointTreeFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Every point within `radiusMetres` great-circle metres of `(lon, lat)`, as a
     * `{ indices, distances }` object.
     * @param {number} lon
     * @param {number} lat
     * @param {number} radius_metres
     * @returns {Neighbors}
     */
    withinDistance(lon, lat, radius_metres) {
        const ret = wasm.pointtree_withinDistance(this.__wbg_ptr, lon, lat, radius_metres);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Every point within `radiusMetres` WGS84-ellipsoid geodesic metres of `(lon, lat)`,
     * as a `{ indices, distances }` object.
     * @param {number} lon
     * @param {number} lat
     * @param {number} radius_metres
     * @returns {Neighbors}
     */
    withinDistanceWgs84(lon, lat, radius_metres) {
        const ret = wasm.pointtree_withinDistanceWgs84(this.__wbg_ptr, lon, lat, radius_metres);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
}
if (Symbol.dispose) PointTree.prototype[Symbol.dispose] = PointTree.prototype.free;

/**
 * A geodetic R-tree over polygons.
 */
export class PolygonTree {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        PolygonTreeFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_polygontree_free(ptr, 0);
    }
    /**
     * The number of polygons in the tree.
     * @returns {number}
     */
    get length() {
        const ret = wasm.polygontree_length(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * The input position of the polygon nearest to `(lon, lat)` by great-circle
     * distance (zero if the point is inside one), or `undefined` if the tree is empty.
     * @param {number} lon
     * @param {number} lat
     * @returns {number | undefined}
     */
    nearest(lon, lat) {
        const ret = wasm.polygontree_nearest(this.__wbg_ptr, lon, lat);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] === Number.MAX_SAFE_INTEGER ? undefined : ret[0];
    }
    /**
     * The `k` nearest polygons to `(lon, lat)` by great-circle distance, nearest first,
     * as a `{ indices, distances }` object. Fewer than `k` are returned if the tree
     * holds fewer polygons.
     * @param {number} lon
     * @param {number} lat
     * @param {number} k
     * @returns {Neighbors}
     */
    nearestN(lon, lat, k) {
        const ret = wasm.polygontree_nearestN(this.__wbg_ptr, lon, lat, k);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * The polygon nearest to `(lon, lat)` as a `{ index, distance }` object (distance
     * zero if the point is inside it), or `undefined` if the tree is empty.
     * @param {number} lon
     * @param {number} lat
     * @returns {Neighbor | undefined}
     */
    nearestWithDistance(lon, lat) {
        const ret = wasm.polygontree_nearestWithDistance(this.__wbg_ptr, lon, lat);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Builds a tree from interleaved `[lon0, lat0, lon1, lat1, ...]` vertex coordinates
     * in degrees and a two-level CSR layout: `ringOffsets` partitions the vertices into
     * rings and `polygonOffsets` partitions the rings into polygons, each with one entry
     * per part plus a final end marker. Within a polygon the first ring is the exterior
     * and the rest are holes (the GeoArrow layout). Throws a `GeodeticError` on an
     * invalid offset array, a polygon with no rings, an out-of-range or non-finite
     * coordinate, or a ring that is not closed or has too few vertices.
     * @param {Float64Array} coords
     * @param {Uint32Array} ring_offsets
     * @param {Uint32Array} polygon_offsets
     */
    constructor(coords, ring_offsets, polygon_offsets) {
        const ptr0 = passArrayF64ToWasm0(coords, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray32ToWasm0(ring_offsets, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray32ToWasm0(polygon_offsets, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.polygontree_new(ptr0, len0, ptr1, len1, ptr2, len2);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        PolygonTreeFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Every polygon within `radiusMetres` great-circle metres of `(lon, lat)` (including
     * any containing it), as a `{ indices, distances }` object.
     * @param {number} lon
     * @param {number} lat
     * @param {number} radius_metres
     * @returns {Neighbors}
     */
    withinDistance(lon, lat, radius_metres) {
        const ret = wasm.polygontree_withinDistance(this.__wbg_ptr, lon, lat, radius_metres);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
}
if (Symbol.dispose) PolygonTree.prototype[Symbol.dispose] = PolygonTree.prototype.free;

/**
 * Installs the panic hook once, when the module is instantiated.
 */
export function start() {
    wasm.start();
}
function __wbg_get_imports() {
    const import0 = {
        __proto__: null,
        __wbg___wbindgen_debug_string_a57024b9c6e4a48b: function(arg0, arg1) {
            const ret = debugString(arg1);
            const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg___wbindgen_throw_bb96b2010945f0bc: function(arg0, arg1) {
            throw new Error(getStringFromWasm0(arg0, arg1));
        },
        __wbg_error_757e9472f8410341: function(arg0, arg1) {
            let deferred0_0;
            let deferred0_1;
            try {
                deferred0_0 = arg0;
                deferred0_1 = arg1;
                console.error(getStringFromWasm0(arg0, arg1));
            } finally {
                wasm.__wbindgen_free(deferred0_0, deferred0_1, 1);
            }
        },
        __wbg_new_227d7c05414eb861: function() {
            const ret = new Error();
            return ret;
        },
        __wbg_new_358857d90afd5a2d: function(arg0, arg1) {
            const ret = new Error(getStringFromWasm0(arg0, arg1));
            return ret;
        },
        __wbg_new_ebe3e0f6837f0879: function() {
            const ret = new Object();
            return ret;
        },
        __wbg_new_from_slice_8aed4f0384605526: function(arg0, arg1) {
            const ret = new Uint32Array(getArrayU32FromWasm0(arg0, arg1));
            return ret;
        },
        __wbg_new_from_slice_f6e95bc2809a2b07: function(arg0, arg1) {
            const ret = new Float64Array(getArrayF64FromWasm0(arg0, arg1));
            return ret;
        },
        __wbg_set_8155bb79a948541b: function() { return handleError(function (arg0, arg1, arg2) {
            const ret = Reflect.set(arg0, arg1, arg2);
            return ret;
        }, arguments); },
        __wbg_set_name_7a2a05b77a392440: function(arg0, arg1, arg2) {
            arg0.name = getStringFromWasm0(arg1, arg2);
        },
        __wbg_stack_3b0d974bbf31e44f: function(arg0, arg1) {
            const ret = arg1.stack;
            const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbindgen_cast_0000000000000001: function(arg0) {
            // Cast intrinsic for `F64 -> Externref`.
            const ret = arg0;
            return ret;
        },
        __wbindgen_cast_0000000000000002: function(arg0, arg1) {
            // Cast intrinsic for `Ref(String) -> Externref`.
            const ret = getStringFromWasm0(arg0, arg1);
            return ret;
        },
        __wbindgen_init_externref_table: function() {
            const table = wasm.__wbindgen_externrefs;
            const offset = table.grow(4);
            table.set(0, undefined);
            table.set(offset + 0, undefined);
            table.set(offset + 1, null);
            table.set(offset + 2, true);
            table.set(offset + 3, false);
        },
    };
    return {
        __proto__: null,
        "./rstar_geodetic_wasm_bg.js": import0,
    };
}

const LineStringTreeFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_linestringtree_free(ptr, 1));
const PointTreeFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_pointtree_free(ptr, 1));
const PolygonTreeFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_polygontree_free(ptr, 1));

function addToExternrefTable0(obj) {
    const idx = wasm.__externref_table_alloc();
    wasm.__wbindgen_externrefs.set(idx, obj);
    return idx;
}

function debugString(val) {
    // primitive types
    const type = typeof val;
    if (type == 'number' || type == 'boolean' || val == null) {
        return  `${val}`;
    }
    if (type == 'string') {
        return `"${val}"`;
    }
    if (type == 'symbol') {
        const description = val.description;
        if (description == null) {
            return 'Symbol';
        } else {
            return `Symbol(${description})`;
        }
    }
    if (type == 'function') {
        const name = val.name;
        if (typeof name == 'string' && name.length > 0) {
            return `Function(${name})`;
        } else {
            return 'Function';
        }
    }
    // objects
    if (Array.isArray(val)) {
        const length = val.length;
        let debug = '[';
        if (length > 0) {
            debug += debugString(val[0]);
        }
        for(let i = 1; i < length; i++) {
            debug += ', ' + debugString(val[i]);
        }
        debug += ']';
        return debug;
    }
    // Test for built-in
    const builtInMatches = /\[object ([^\]]+)\]/.exec(toString.call(val));
    let className;
    if (builtInMatches && builtInMatches.length > 1) {
        className = builtInMatches[1];
    } else {
        // Failed to match the standard '[object ClassName]'
        return toString.call(val);
    }
    if (className == 'Object') {
        // we're a user defined class or Object
        // JSON.stringify avoids problems with cycles, and is generally much
        // easier than looping through ownProperties of `val`.
        try {
            return 'Object(' + JSON.stringify(val) + ')';
        } catch (_) {
            return 'Object';
        }
    }
    // errors
    if (val instanceof Error) {
        return `${val.name}: ${val.message}\n${val.stack}`;
    }
    // TODO we could test for more things here, like `Set`s and `Map`s.
    return className;
}

function getArrayF64FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getFloat64ArrayMemory0().subarray(ptr / 8, ptr / 8 + len);
}

function getArrayU32FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getUint32ArrayMemory0().subarray(ptr / 4, ptr / 4 + len);
}

let cachedDataViewMemory0 = null;
function getDataViewMemory0() {
    if (cachedDataViewMemory0 === null || cachedDataViewMemory0.buffer.detached === true || (cachedDataViewMemory0.buffer.detached === undefined && cachedDataViewMemory0.buffer !== wasm.memory.buffer)) {
        cachedDataViewMemory0 = new DataView(wasm.memory.buffer);
    }
    return cachedDataViewMemory0;
}

let cachedFloat64ArrayMemory0 = null;
function getFloat64ArrayMemory0() {
    if (cachedFloat64ArrayMemory0 === null || cachedFloat64ArrayMemory0.byteLength === 0) {
        cachedFloat64ArrayMemory0 = new Float64Array(wasm.memory.buffer);
    }
    return cachedFloat64ArrayMemory0;
}

function getStringFromWasm0(ptr, len) {
    return decodeText(ptr >>> 0, len);
}

let cachedUint32ArrayMemory0 = null;
function getUint32ArrayMemory0() {
    if (cachedUint32ArrayMemory0 === null || cachedUint32ArrayMemory0.byteLength === 0) {
        cachedUint32ArrayMemory0 = new Uint32Array(wasm.memory.buffer);
    }
    return cachedUint32ArrayMemory0;
}

let cachedUint8ArrayMemory0 = null;
function getUint8ArrayMemory0() {
    if (cachedUint8ArrayMemory0 === null || cachedUint8ArrayMemory0.byteLength === 0) {
        cachedUint8ArrayMemory0 = new Uint8Array(wasm.memory.buffer);
    }
    return cachedUint8ArrayMemory0;
}

function handleError(f, args) {
    try {
        return f.apply(this, args);
    } catch (e) {
        const idx = addToExternrefTable0(e);
        wasm.__wbindgen_exn_store(idx);
    }
}

function passArray32ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 4, 4) >>> 0;
    getUint32ArrayMemory0().set(arg, ptr / 4);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passArrayF64ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 8, 8) >>> 0;
    getFloat64ArrayMemory0().set(arg, ptr / 8);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passStringToWasm0(arg, malloc, realloc) {
    if (realloc === undefined) {
        const buf = cachedTextEncoder.encode(arg);
        const ptr = malloc(buf.length, 1) >>> 0;
        getUint8ArrayMemory0().subarray(ptr, ptr + buf.length).set(buf);
        WASM_VECTOR_LEN = buf.length;
        return ptr;
    }

    let len = arg.length;
    let ptr = malloc(len, 1) >>> 0;

    const mem = getUint8ArrayMemory0();

    let offset = 0;

    for (; offset < len; offset++) {
        const code = arg.charCodeAt(offset);
        if (code > 0x7F) break;
        mem[ptr + offset] = code;
    }
    if (offset !== len) {
        if (offset !== 0) {
            arg = arg.slice(offset);
        }
        ptr = realloc(ptr, len, len = offset + arg.length * 3, 1) >>> 0;
        const view = getUint8ArrayMemory0().subarray(ptr + offset, ptr + len);
        const ret = cachedTextEncoder.encodeInto(arg, view);

        offset += ret.written;
        ptr = realloc(ptr, len, offset, 1) >>> 0;
    }

    WASM_VECTOR_LEN = offset;
    return ptr;
}

function takeFromExternrefTable0(idx) {
    const value = wasm.__wbindgen_externrefs.get(idx);
    wasm.__externref_table_dealloc(idx);
    return value;
}

let cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
cachedTextDecoder.decode();
const MAX_SAFARI_DECODE_BYTES = 2146435072;
let numBytesDecoded = 0;
function decodeText(ptr, len) {
    numBytesDecoded += len;
    if (numBytesDecoded >= MAX_SAFARI_DECODE_BYTES) {
        cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
        cachedTextDecoder.decode();
        numBytesDecoded = len;
    }
    return cachedTextDecoder.decode(getUint8ArrayMemory0().subarray(ptr, ptr + len));
}

const cachedTextEncoder = new TextEncoder();

if (!('encodeInto' in cachedTextEncoder)) {
    cachedTextEncoder.encodeInto = function (arg, view) {
        const buf = cachedTextEncoder.encode(arg);
        view.set(buf);
        return {
            read: arg.length,
            written: buf.length
        };
    };
}

let WASM_VECTOR_LEN = 0;

let wasmModule, wasmInstance, wasm;
function __wbg_finalize_init(instance, module) {
    wasmInstance = instance;
    wasm = instance.exports;
    wasmModule = module;
    cachedDataViewMemory0 = null;
    cachedFloat64ArrayMemory0 = null;
    cachedUint32ArrayMemory0 = null;
    cachedUint8ArrayMemory0 = null;
    wasm.__wbindgen_start();
    return wasm;
}

async function __wbg_load(module, imports) {
    if (typeof Response === 'function' && module instanceof Response) {
        if (!module.ok) {
            throw new Error(`failed to fetch Wasm: ${module.status} ${module.statusText} fetching '${module.url}'`);
        }

        if (typeof WebAssembly.instantiateStreaming === 'function') {
            try {
                return await WebAssembly.instantiateStreaming(module, imports);
            } catch (e) {
                const validResponse = expectedResponseType(module.type);

                if (validResponse && module.headers.get('Content-Type') !== 'application/wasm') {
                    console.warn("`WebAssembly.instantiateStreaming` failed because your server does not serve Wasm with `application/wasm` MIME type. Falling back to `WebAssembly.instantiate` which is slower. Original error:\n", e);

                } else { throw e; }
            }
        }

        const bytes = await module.arrayBuffer();
        return await WebAssembly.instantiate(bytes, imports);
    } else {
        const instance = await WebAssembly.instantiate(module, imports);

        if (instance instanceof WebAssembly.Instance) {
            return { instance, module };
        } else {
            return instance;
        }
    }

    function expectedResponseType(type) {
        switch (type) {
            case 'basic': case 'cors': case 'default': return true;
        }
        return false;
    }
}

function initSync(module) {
    if (wasm !== undefined) return wasm;


    if (module !== undefined) {
        if (Object.getPrototypeOf(module) === Object.prototype) {
            ({module} = module)
        } else {
            console.warn('using deprecated parameters for `initSync()`; pass a single object instead')
        }
    }

    const imports = __wbg_get_imports();
    if (!(module instanceof WebAssembly.Module)) {
        module = new WebAssembly.Module(module);
    }
    const instance = new WebAssembly.Instance(module, imports);
    return __wbg_finalize_init(instance, module);
}

async function __wbg_init(module_or_path) {
    if (wasm !== undefined) return wasm;


    if (module_or_path !== undefined) {
        if (Object.getPrototypeOf(module_or_path) === Object.prototype) {
            ({module_or_path} = module_or_path)
        } else {
            console.warn('using deprecated parameters for the initialization function; pass a single object instead')
        }
    }

    if (module_or_path === undefined) {
        module_or_path = new URL('rstar_geodetic_wasm_bg.wasm', import.meta.url);
    }
    const imports = __wbg_get_imports();

    if (typeof module_or_path === 'string' || (typeof Request === 'function' && module_or_path instanceof Request) || (typeof URL === 'function' && module_or_path instanceof URL)) {
        module_or_path = fetch(module_or_path);
    }

    const { instance, module } = await __wbg_load(await module_or_path, imports);

    return __wbg_finalize_init(instance, module);
}

export { initSync, __wbg_init as default };
